import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Type, Plus, Sparkles, Loader2, Globe, ShieldAlert, Check, X, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_STYLES = {
  low: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  high: 'bg-red-500/10 text-red-400 border-red-500/30',
  unknown: 'bg-white/[0.05] text-muted-foreground border-border',
};

const STATUS_TABS = [
  { id: 'pending', label: 'New' },
  { id: 'shortlisted', label: 'Shortlisted' },
  { id: 'rejected', label: 'Rejected' },
];

const NamingStudioPanel = () => {
  const { currentProject } = useProject();

  const [idea, setIdea] = useState('');
  const [industry, setIndustry] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [generating, setGenerating] = useState(false);
  const [fetching, setFetching] = useState(false);

  const fetchCandidates = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/naming`);
      setCandidates(res.data);
    } catch (error) {
      console.error('Failed to fetch name candidates:', error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
    // Pre-fill from the project's canonical business description (set once
    // in Business Overview / the wizard's first step) instead of asking the
    // user to retype the same idea here -- still fully editable before generating.
    if (currentProject) {
      setIdea(currentProject.description || '');
      setIndustry(currentProject.industry || '');
    }
  }, [currentProject?.id]);

  const addKeyword = () => {
    const value = keywordInput.trim();
    if (value && !keywords.includes(value)) {
      setKeywords([...keywords, value]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (value) => {
    setKeywords(keywords.filter((k) => k !== value));
  };

  const handleGenerate = async () => {
    if (!idea.trim()) {
      toast.error('Describe the business idea first');
      return;
    }
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/projects/${currentProject.id}/naming/generate`, {
        idea,
        industry: industry || undefined,
        keywords,
        count: 8,
      });
      setCandidates((prev) => [...res.data, ...prev]);
      setStatusFilter('pending');
      toast.success(`Generated ${res.data.length} name candidates`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate names');
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (candidateId, status) => {
    try {
      const res = await axios.patch(
        `${API}/projects/${currentProject.id}/naming/${candidateId}`,
        { status }
      );
      setCandidates((prev) => prev.map((c) => (c.id === candidateId ? res.data : c)));
    } catch (error) {
      toast.error('Failed to update candidate');
    }
  };

  const visibleCandidates = candidates.filter((c) => c.status === statusFilter);

  if (!currentProject) return null;

  return (
    <div className="h-full overflow-auto p-6" data-testid="naming-studio-panel">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Naming Studio</h2>
              <p className="text-muted-foreground">Generate brandable name candidates with live domain and trademark-risk checks</p>
            </div>
            <ReportButton section="naming" projectId={currentProject.id} />
          </div>

          {/* Intake Section */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Type className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Business Idea</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-foreground/80">Describe the business</Label>
                <textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="e.g., A subscription coffee brand for remote software developers, focused on focus/energy and a minimalist aesthetic"
                  rows={3}
                  className="w-full rounded-md bg-white/[0.05] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  data-testid="naming-idea-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Industry (optional)</Label>
                <Input
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g., Food & Beverage"
                  className="bg-white/[0.05] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="naming-industry-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Keywords (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="e.g., focus, minimal"
                    className="bg-white/[0.05] border-border text-foreground placeholder:text-muted-foreground/70"
                    data-testid="naming-keyword-input"
                  />
                  <Button
                    onClick={addKeyword}
                    variant="outline"
                    className="bg-white/[0.05] border-border text-foreground hover:bg-white/[0.08]"
                    data-testid="naming-add-keyword-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-border text-sm text-foreground flex items-center gap-2 group"
                  >
                    {keyword}
                    <button
                      onClick={() => removeKeyword(keyword)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                      data-testid={`naming-remove-keyword-${index}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
              data-testid="naming-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Names
                </>
              )}
            </Button>
          </div>

          {/* Status filter tabs */}
          <div className="flex items-center gap-2 mb-4">
            {STATUS_TABS.map((tab) => {
              const count = candidates.filter((c) => c.status === tab.id).length;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    statusFilter === tab.id
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-white/[0.03] text-muted-foreground border-border hover:text-foreground'
                  }`}
                  data-testid={`naming-status-tab-${tab.id}`}
                >
                  {tab.label} {count > 0 && <span className="opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>

          {/* Candidates */}
          {fetching && candidates.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : visibleCandidates.length === 0 ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Type className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">
                {candidates.length === 0
                  ? 'No name candidates yet. Describe your business idea above and generate some.'
                  : `No ${statusFilter} candidates.`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleCandidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className="glass rounded-2xl p-5 border border-border"
                  data-testid={`naming-candidate-${candidate.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="font-heading text-xl text-foreground">{candidate.name}</h4>
                    <span
                      className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wide border font-mono ${
                        RISK_STYLES[candidate.trademark_risk] || RISK_STYLES.unknown
                      }`}
                      title={candidate.trademark_note}
                    >
                      <ShieldAlert className="w-3 h-3 inline mr-1 -mt-0.5" />
                      {candidate.trademark_risk} risk
                    </span>
                  </div>

                  {candidate.rationale && (
                    <p className="text-sm text-muted-foreground mb-1">{candidate.rationale}</p>
                  )}
                  <ProviderBadge provider={candidate.generated_by} className="block mb-3" />

                  <div className="flex flex-wrap gap-2 mb-4">
                    {(candidate.domains || []).map((d) => (
                      <span
                        key={d.tld}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono border ${
                          d.available === true
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                            : d.available === false
                            ? 'bg-white/[0.04] text-muted-foreground border-border line-through'
                            : 'bg-white/[0.04] text-muted-foreground border-border'
                        }`}
                        title={d.note}
                      >
                        <Globe className="w-3 h-3" />
                        .{d.tld}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    {candidate.status !== 'shortlisted' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(candidate.id, 'shortlisted')}
                        className="bg-white/[0.05] border-border text-foreground hover:bg-white/[0.08]"
                        data-testid={`naming-shortlist-${candidate.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1.5" />
                        Shortlist
                      </Button>
                    )}
                    {candidate.status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(candidate.id, 'rejected')}
                        className="bg-white/[0.05] border-border text-foreground hover:bg-white/[0.08]"
                        data-testid={`naming-reject-${candidate.id}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1.5" />
                        Reject
                      </Button>
                    )}
                    {candidate.status !== 'pending' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateStatus(candidate.id, 'pending')}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`naming-reset-${candidate.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default NamingStudioPanel;
